/**
 * Pay Yourself First model. Auto-saves hourlyRate per workday off the
 * top — that reserve is subtracted from the running balance, plus
 * accounted for in `goalToDate` / `goalPeriod` progress.
 */

import type { AppData } from '../../data/types';
import type { PayPeriod } from '../payPeriod';
import {
  computeRolloverAmount,
  isWorkday,
  resolvePeriodIncome,
  resolveWorkDaysPerWeek,
  roundMoney,
  sumExpensesInRange,
  sumExpensesInRangeWhere,
  sumIncomeInRange,
  sumSpendingWithScheduledInRange,
  workdaysInRange,
} from './shared';
import { _classifyDailyStatus, type DailyStatus } from './daily';

export interface PayYourselfFirstResult {
  period: PayPeriod;
  // Running-balance fields (fallback when no accounts are linked)
  availableToday: number;
  dailyIncomeRate: number;
  remainingThisPeriod: number;
  dayInPeriod: number;
  totalDays: number;
  daysRemaining: number;
  spentToday: number;
  spentThisPeriod: number;
  status: DailyStatus;
  statusLabel: string;
  periodIncome: number;
  rolloverAmount: number;
  // PYF display values
  hourlyRate: number;
  dailyGoal: number;
  todayGoal: number;
  isTodayWorkday: boolean;
  weeklyGoal: number;
  monthlyGoal: number;
  yearlyGoal: number;
  savedToday: number;
  savedThisPeriod: number;
  goalToDate: number;
  /** Full-period target (= periodGoal). */
  goalPeriod: number;
  /** @deprecated alias of goalPeriod, kept for back-compat. */
  periodGoal: number;
  workdaysElapsed: number;
  workdaysTotal: number;
  /** savedThisPeriod / goalToDate, clamped 0..1. */
  progress: number;
  /** savedThisPeriod / goalPeriod, clamped 0..1. */
  periodProgress: number;
  /** PYF still to be auto-saved between today and period end. */
  pyfRemaining: number;
}

export function calculatePYF(
  data: AppData,
  period: PayPeriod,
  today: Date,
): PayYourselfFirstResult {
  const hourlyRate = data.profile.hourlyRate ?? 0;
  const wdpw = resolveWorkDaysPerWeek(data.profile.workDaysPerWeek);
  const isPyfActive = data.profile.budgetModel === 'pyf' && hourlyRate > 0;

  const dailyGoal = roundMoney(hourlyRate);
  const weeklyGoal = roundMoney(hourlyRate * wdpw);
  const monthlyGoal = roundMoney(hourlyRate * wdpw * (52 / 12));
  const yearlyGoal = roundMoney(hourlyRate * wdpw * 52);

  const isTodayWorkday = isWorkday(today, wdpw);
  const workdaysElapsed = isPyfActive
    ? workdaysInRange(period.start, today, wdpw)
    : 0;
  const workdaysTotal = isPyfActive
    ? workdaysInRange(period.start, period.end, wdpw)
    : 0;

  const savingsCategoryIds = new Set(
    data.categories
      .filter((c) => c.bucket503020 === 'savings' || c.icon === 'PiggyBank')
      .map((c) => c.id),
  );
  const manualSavedThisPeriod = sumExpensesInRangeWhere(
    data.transactions,
    period.start,
    today,
    (t) => savingsCategoryIds.has(t.categoryId),
  );
  const manualSavedToday = sumExpensesInRangeWhere(
    data.transactions,
    today,
    today,
    (t) => savingsCategoryIds.has(t.categoryId),
  );

  const autoSavedToday =
    isPyfActive && isTodayWorkday ? hourlyRate : 0;
  const autoSavedThisPeriod = isPyfActive
    ? roundMoney(hourlyRate * workdaysElapsed)
    : 0;

  const savedToday = roundMoney(autoSavedToday + manualSavedToday);
  const savedThisPeriod = roundMoney(
    autoSavedThisPeriod + manualSavedThisPeriod,
  );

  const goalToDate = isPyfActive
    ? roundMoney(dailyGoal * workdaysElapsed)
    : roundMoney(dailyGoal * period.dayInPeriod);
  const goalPeriod = isPyfActive
    ? roundMoney(dailyGoal * workdaysTotal)
    : roundMoney(dailyGoal * period.totalDays);

  const progress =
    goalToDate > 0 ? Math.min(1, Math.max(0, savedThisPeriod / goalToDate)) : 0;
  const periodProgress =
    goalPeriod > 0 ? Math.min(1, Math.max(0, savedThisPeriod / goalPeriod)) : 0;

  const todayGoal = isTodayWorkday ? dailyGoal : 0;
  const pyfRemaining = Math.max(0, roundMoney(goalPeriod - goalToDate));

  // Running-balance fallback
  const periodIncome = resolvePeriodIncome(data, period);
  const rolloverAmount = computeRolloverAmount(data, period);
  const incomeToDate = sumIncomeInRange(data.transactions, period.start, today);
  // Past scheduled bills auto-deduct on their due date — included
  // alongside transaction-based spending in the running balance.
  const spendingToDate = sumSpendingWithScheduledInRange(
    data,
    period.start,
    today,
  );
  const pyfToDate = autoSavedThisPeriod;
  const availableToday = roundMoney(
    rolloverAmount + incomeToDate - pyfToDate - spendingToDate,
  );
  const dailyIncomeRate =
    period.totalDays > 0 ? roundMoney(periodIncome / period.totalDays) : 0;
  const remainingThisPeriod = roundMoney(
    periodIncome - spendingToDate - pyfToDate,
  );
  const spentToday = sumExpensesInRange(data.transactions, today, today);
  const spentThisPeriod = spendingToDate;
  const { status, statusLabel } = _classifyDailyStatus(
    availableToday,
    dailyIncomeRate,
  );

  return {
    period,
    availableToday,
    dailyIncomeRate,
    remainingThisPeriod,
    dayInPeriod: period.dayInPeriod,
    totalDays: period.totalDays,
    daysRemaining: period.daysRemaining,
    spentToday,
    spentThisPeriod,
    status,
    statusLabel,
    periodIncome,
    rolloverAmount,
    hourlyRate,
    dailyGoal,
    todayGoal,
    isTodayWorkday,
    weeklyGoal,
    monthlyGoal,
    yearlyGoal,
    savedToday,
    savedThisPeriod,
    goalToDate,
    goalPeriod,
    periodGoal: goalPeriod,
    workdaysElapsed,
    workdaysTotal,
    progress,
    periodProgress,
    pyfRemaining,
  };
}
